import { Suspense, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useBlocker, useSearchParams } from "react-router"
import { useDebounce } from "@uidotdev/usehooks"
import { validate } from "uuid"
import { useUser } from "@/apis/bff"
import { DrawerWrapper } from "@/components/drawer-wrapper"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { Header } from "@/layouts/header"
import { getInsensitiveSearchParams } from "@/lib/get-insensitive-search-params"
import { convertISOCodeToLanguage } from "@/lib/language-to-iso-code"
import { getCleanPhoneNumber } from "@/lib/utils"
import { NewsBannerNotify } from "@/modules/admin/news/components/news-banner-notify"
import { NewPatientWizard } from "@/modules/inbound-wizard"
import { LeadsOnDutyBanner } from "@/modules/leads-on-duty/components/leads-on-duty-banner"
import CountryDropdown from "@/modules/patients/components/country-dropdown"
import PatientEmpty from "@/modules/patients/components/patient-table-empty"
import { PatientsSearch } from "@/modules/patients/components/patients-search"
import { TelephonySearchParams } from "@/modules/patients/constants/Patient"
import { HealthInsuranceCardNumberMissingToggle } from "@/modules/patients/patient-list/health-insurance-card-number-missing-toggle"
import { PatientTableContainer } from "@/modules/patients/patient-list/patient-table-container"
import { PotentialDuplicatesToggle } from "@/modules/patients/patient-list/potential-duplicates-toggle"
import { useChSetCallerConsentMutation } from "@/modules/patients/services/ch/useChSetCallerConsentMutation"
import { CallLineBanner } from "@/modules/telephony/call-line-banner.tsx"
import { Notes } from "@/modules/temporary-notes/notes.tsx"
import useCallContextStore from "@/stores/useCallContextStore"
import { Country } from "@/types/__generated__/union-types"

const MIN_SEARCH_CHARACTERS = 3

const useClearCallStoreWhenLeavingPatientsListOrMedicalRecord = () => {
  const clearCallStore = useCallContextStore((state) => state.clearStore)

  const blocker = useBlocker(({ nextLocation }) => {
    return !nextLocation.pathname.startsWith("/patients")
  })

  useEffect(() => {
    if (blocker.state === "blocked") {
      clearCallStore()
      blocker.proceed()
    }
  }, [blocker, clearCallStore])
}

const Patients = () => {
  const [chSetCallerConsentMutation] = useChSetCallerConsentMutation()
  const [searchParams] = useSearchParams()
  const countryParams = getInsensitiveSearchParams(searchParams, TelephonySearchParams.Country).value?.toUpperCase() as
    | Country
    | undefined
  const callLine = getInsensitiveSearchParams(searchParams, TelephonySearchParams.CallLine).value
  const record = getInsensitiveSearchParams(searchParams, TelephonySearchParams.Record).value
  const callLineLanguage = convertISOCodeToLanguage(
    getInsensitiveSearchParams(searchParams, TelephonySearchParams.Language)?.value?.toUpperCase(),
  )
  const callID = getInsensitiveSearchParams(searchParams, TelephonySearchParams.CallId).value
  const payline = getInsensitiveSearchParams(searchParams, TelephonySearchParams.PayLine).value

  const user = useUser()

  const [patientQuery, setPatientQuery] = useState("")
  const [hasFilterChanged, setHasFilterChanged] = useState(false)
  const debouncedPatientQuery = useDebounce(patientQuery, 350)
  const [countryFilter, setCountryFilter] = useState<Country>(
    () => user.countries.find((c) => c === countryParams) ?? user.countries[0],
  )
  const [isHealthInsuranceCardNumberMissing, setIsHealthInsuranceCardNumberMissing] = useState<boolean>(false)
  const [isPotentialDuplicateFilter, setIsPotentialDuplicateFilter] = useState<boolean>(false)
  const callerPhoneNumber = useCallContextStore((state) => state.callerPhoneNumber)
  const callLineCode = useCallContextStore((state) => state.callLineCode)
  const setCountry = useCallContextStore((state) => state.setCountry)
  const clearCallStore = useCallContextStore((state) => state.clearStore)
  const toggleKidsLine = useCallContextStore((state) => state.toggleKidsLine)
  const setCallLineCode = useCallContextStore((state) => state.setCallLineCode)
  const setRecord = useCallContextStore((state) => state.setRecord)
  const setCallLineLanguage = useCallContextStore((state) => state.setCallLineLanguage)
  const setCallID = useCallContextStore((state) => state.setCallID)
  const setPayline = useCallContextStore((state) => state.setPayline)
  const setStartedOn = useCallContextStore((state) => state.setStartedOn)

  const { t } = useTranslation()

  useEffect(() => {
    if (callID) {
      FaroInsightsWrapper.pushEvent("ibc_telephony_push", {
        callID: callID,
        callLineLanguage: callLineLanguage ?? "",
      })
    }
    setRecord(record)
    setCallLineLanguage(callLineLanguage)
    setCallID(callID)
    setPayline(payline)
    setStartedOn()
  }, [record, callLineLanguage, callID, setCallID, setRecord, setCallLineLanguage, setPayline, payline, setStartedOn])

  useEffect(() => {
    if (countryParams === "CH") {
      if (callID && validate(callID)) {
        chSetCallerConsentMutation({
          variables: {
            input: { callRecordingId: callID, accepted: record === "1", language: callLineLanguage ?? "ENGLISH" },
          },
        })
      } else {
        console.error("callID is invalid, no consent will be stored", { callID: callID })
      }
    }
  }, [chSetCallerConsentMutation, countryParams, callID, record, callLineLanguage])

  useEffect(() => {
    if (callID && countryParams) {
      setCountry(countryParams)
      if (callLine) {
        const callLineCode = callLine.split(" ")
        setCallLineCode(callLineCode[0])
      } else {
        toggleKidsLine(false)
        setCallLineCode("")
      }
    } else {
      clearCallStore()
    }
  }, [countryParams, callLine, callID, setCountry, clearCallStore, setCallLineCode, toggleKidsLine])

  useClearCallStoreWhenLeavingPatientsListOrMedicalRecord()

  const phoneNumber = !hasFilterChanged && callerPhoneNumber ? getCleanPhoneNumber(callerPhoneNumber) : ""
  const isFirstAccess = !hasFilterChanged && !debouncedPatientQuery.length && !callerPhoneNumber
  const isSearchCriteriaInsufficient =
    hasFilterChanged &&
    debouncedPatientQuery.length < MIN_SEARCH_CHARACTERS &&
    (!isHealthInsuranceCardNumberMissing || countryFilter === "DE") &&
    !isPotentialDuplicateFilter

  const setPatientQueryText = (value: string) => {
    setPatientQuery(value)
    setHasFilterChanged(true)
  }

  return (
    <>
      <Header title={t("patient.header.patients")}>
        <div className="flex gap-2">
          <NewPatientWizard
            defaultCountry={countryFilter}
            patientQuery={patientQuery}
            setPatientQuery={setPatientQueryText}
          />
        </div>
      </Header>
      <div className="@container flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <NewsBannerNotify />
          <LeadsOnDutyBanner />
          {callLineCode.length > 0 && <CallLineBanner variant="warning" testId="call-line-banner" />}
          <div className="bg-subdued flex w-full items-center justify-between gap-4 p-8">
            <div className="flex space-x-4">
              <CountryDropdown
                value={countryFilter}
                readOnly={user.countries.length <= 1}
                isDisabled={(item) => !user.countries.includes(item)}
                onChange={(value) => {
                  setCountryFilter(value)
                  setCountry(value)
                  setHasFilterChanged(true)
                }}
              />
              {user.isOneOf(["CustomerServiceCenter"]) && (
                <>
                  <HealthInsuranceCardNumberMissingToggle
                    disabled={countryFilter === "DE"}
                    checked={isHealthInsuranceCardNumberMissing}
                    onClick={() => {
                      setIsHealthInsuranceCardNumberMissing((prev) => !prev)
                      setHasFilterChanged(true)
                    }}
                  />
                  <PotentialDuplicatesToggle
                    checked={isPotentialDuplicateFilter}
                    onClick={() => {
                      setIsPotentialDuplicateFilter((prev) => !prev)
                      setHasFilterChanged(true)
                    }}
                  />
                </>
              )}
            </div>
            <PatientsSearch
              value={patientQuery}
              placeholder={t("common.search")}
              autoFocus
              onClear={() => setPatientQuery("")}
              onChange={(event) => {
                setPatientQuery(event.target.value)
                setHasFilterChanged(true)
              }}
            />
          </div>
          <Suspense
            fallback={
              <PatientEmpty
                title={t("patient.table.notYetSearching")}
                message={t("patient.table.notYetSearchingDescription")}
                isLoading
              />
            }>
            {isFirstAccess || isSearchCriteriaInsufficient ? (
              <PatientEmpty
                title={t("patient.table.notYetSearching")}
                message={t("patient.table.notYetSearchingDescription")}
              />
            ) : (
              <PatientTableContainer
                key={countryFilter}
                country={countryFilter}
                keyword={debouncedPatientQuery}
                isHealthInsuranceCardNumberMissing={countryFilter === "DE" ? null : isHealthInsuranceCardNumberMissing}
                isPotentialDuplicate={isPotentialDuplicateFilter || null}
                phoneNumber={phoneNumber}
                hasFilterChanged={hasFilterChanged}
                setPatientQuery={setPatientQuery}
              />
            )}
          </Suspense>
        </div>
        <Suspense fallback={null}>
          <DrawerWrapper containerId="patients-drawer" className="[&>div]:h-full [&>div]:w-125" />
        </Suspense>
      </div>
      {((callID && countryParams) || callID) && <Notes />}
    </>
  )
}
export { Patients }
